import React, { Component } from 'react';
import Select from './common/select';
import { HTTPService } from "./../services/httpService";
import { filterSpecialistsForDealer } from '../util/specialistDiscovery';
import { isMarineDealer } from '../util/marineCapability';

const API = new HTTPService()

export default class SpecialistForm extends Component {
    // Instance variable to store display name to specialist mapping
    specialistDisplayMap = {};

    state = {
        data : {phone : ""},
        errors : {},
        name : "Select the specialist",
        email : "",
        phone : "",
        found : " ",
        customerId: "",
        selectedSpecialist:"Specialist",
        specialists:[],
        // Vendor category state
        selectedVendorCategory: "",
        vendorCategories: [],
        vendorVisibility: "all", // "preferred" or "all"
        filteredSpecialists: [],
        // Loading states
        loadingCategories: false,
        loadingSpecialists: false,
        // Why the specialist list is empty, when it is: 'none' (this dealership
        // has no vendor categories, so the list is never fetched) or 'error'
        // (the categories request itself failed). Null when categories loaded.
        categoriesUnavailable: null,
        // Preferred specialist from dealer preferences
        preferredSpecialistFromDealer: null,
        preferredLocationFromDealer: null
    };

    // Fetch vendor categories for the dealership
    fetchVendorCategories = async () => {
        this.setState({ loadingCategories: true });
        try {
            const response = await API.get(`/dealer/preferences/${this.props.dealerId}`);
            
            // Extract preferred specialist + location if available
            let preferredSpecialist = null;
            let preferredLocation = null;
            if (response.data.length > 0) {
                // Look for default vendor in any category
                for (const category of response.data) {
                    if (category.default_vendor_id) {
                        preferredSpecialist = category.default_vendor_id;
                        preferredLocation = category.default_vendor_location_id || null;
                        break;
                    }
                }
            }

            this.setState({
                vendorCategories: response.data,
                loadingCategories: false,
                categoriesUnavailable: null,
                preferredSpecialistFromDealer: preferredSpecialist,
                preferredLocationFromDealer: preferredLocation
            });
            
            // If there are categories, set the first one as default and fetch specialists
            if (response.data.length > 0) {
                // On resume, open on the draft's saved category so its specialist is
                // in the loaded list; otherwise default to the first category.
                const defaultCategory =
                    (this._pendingResumeSpecialistId && this.props.selectedCategory &&
                        response.data.find(c => c.category_name === this.props.selectedCategory))
                    || response.data[0];
                this.setState({
                    selectedVendorCategory: defaultCategory.category_name,
                    vendorVisibility: defaultCategory.vendor_visibility_mode
                }, () => {
                    this.fetchSpecialistsByCategory(defaultCategory.category_name);
                });
            } else {
                // No categories defined, so there is no real category to send and
                // the specialist list is never fetched. That is a DEAD END for
                // this dealership — nothing in installer_proximity can help,
                // because the request that would read it is never made. Recorded
                // so the picker can say so instead of rendering an empty box.
                console.error(
                    `[SpecialistForm] Dealership ${this.props.dealerId} has no vendor categories, so no specialists can be fetched.`
                );
                this.setState({ categoriesUnavailable: 'none' });
            }
        } catch (error) {
            console.error("Error fetching vendor categories:", error);
            this.setState({ loadingCategories: false, categoriesUnavailable: 'error' });
            // No `response` here (the categories fetch itself failed), so there is
            // no real category to send. Leave the specialist list empty rather than
            // requesting the nonexistent "All Categories" category.
        }
    };

    // Fetch specialists filtered by category from server
    fetchSpecialistsByCategory = async (categoryName) => {
        this.setState({ loadingSpecialists: true });
        try {
            // The one-segment /installerProximity/:dealerId route does not exist
            // server-side — it 404s, the catch below swallows it, and the user
            // sees "No specialists found for the selected category." Always send
            // a category.
            const endpoint =
              `/installerProximity/${this.props.dealerId}/${encodeURIComponent(categoryName)}`;

            const response = await API.get(endpoint);

            // Store all specialists - filtering will be applied in render based on context
            // When visibility is "preferred", only preferred specialists will be shown
            // When visibility is "all", "Any" shows all specialists, but individual options show only preferred
            // Marine dealerships see marine-capable LOCATIONS only. Applied
            // before the preferred/visibility filter below so a starred but
            // car-only location cannot survive it.
            let filteredSpecialists = filterSpecialistsForDealer(
              response.data,
              isMarineDealer(this.props.dealerId)
            );
            
            // If visibility mode is 'preferred', filter to only show dealer_preferred = 1
            // NB: server serializes dealer_preferred as a string ("1"), so coerce before comparing.
            if (this.state.vendorVisibility === "preferred") {
                // Chained off `filteredSpecialists`, NOT `response.data` — re-reading the
                // raw response here would discard the marine filter above and let a starred
                // car-only location survive for a marine dealership.
                filteredSpecialists = filteredSpecialists.filter(specialist => Number(specialist.dealer_preferred) === 1);
            }
            
            // Look up preferred vendor/location for the current category
            const categoryConfig = this.state.vendorCategories.find(cat => cat.category_name === categoryName);
            const catVendorId = categoryConfig?.default_vendor_id || this.state.preferredSpecialistFromDealer;
            const catLocationId = categoryConfig?.default_vendor_location_id || this.state.preferredLocationFromDealer;

            this.setState({
                filteredSpecialists: filteredSpecialists,
                loadingSpecialists: false,
                // Reset specialist selection when category changes
                selectedSpecialist: "Specialist",
                name: "Select the specialist",
                preferredSpecialistFromDealer: catVendorId,
                preferredLocationFromDealer: catLocationId
            }, () => {
                // Auto-select preferred specialist if available
                this.autoSelectPreferredSpecialist();
            });
        } catch (error) {
            console.error("Error fetching specialists:", error);
            this.setState({ 
                filteredSpecialists: [],
                loadingSpecialists: false,
                selectedSpecialist: "Specialist",
                name: "Select the specialist"
            });
        }
    };

    // Select a specialist programmatically (shared by auto-select and single-option logic)
    selectSpecialist = (specialist) => {
        const baseName = specialist.Installer_Name || specialist.installer_name;
        const locationLabel = specialist.location_name || specialist.installer_address_city;
        const parts = [locationLabel ? `${baseName} - ${locationLabel}` : baseName];

        const isDealer = this.props.userType === 'dealer';

        if (isDealer && specialist.Distance !== null && specialist.Distance !== undefined) {
            parts.push(`${Number(specialist.Distance).toFixed(1)} mi`);
        }

        if (isDealer) {
            if (specialist.google_rating && specialist.google_review_count) {
                parts.push(`⭐ ${Number(specialist.google_rating).toFixed(1)} (${specialist.google_review_count})`);
            } else if (specialist.google_rating) {
                parts.push(`⭐ ${Number(specialist.google_rating).toFixed(1)}`);
            }
        }

        const displayName = parts.join(' • ');

        this.setState({
            selectedSpecialist: displayName,
            name: baseName
        });

        const locId = specialist.location_id || specialist.Location_id || null;
        this.props.handleSpecialist(specialist.Installer_id, this.state.selectedVendorCategory, locId);
    };

    // Auto-select preferred specialist if found, or auto-select if only one option
    autoSelectPreferredSpecialist = () => {
        // Resume (one-shot): honor the specialist restored from a draft rather than
        // overriding it with the dealer's preferred vendor. Consumed once so later
        // manual category changes behave normally.
        if (this._pendingResumeSpecialistId) {
            const incoming = this._pendingResumeSpecialistId;
            this._pendingResumeSpecialistId = null;
            if (incoming === "0") {
                const anyLabel = `Any (${this.state.filteredSpecialists.length})`;
                this.setState({ selectedSpecialist: anyLabel, name: 'Any' });
                return;
            }
            const match = this.state.filteredSpecialists.find(
                s => String(s.Installer_id) === incoming
            );
            if (match) {
                this.selectSpecialist(match);
            }
            return;
        }

        const preferredSpecialists = this.state.filteredSpecialists.filter(specialist => Number(specialist.dealer_preferred) === 1);

        // Try to match the dealer's configured preferred vendor+location
        if (this.state.preferredSpecialistFromDealer) {
            let foundPreferred = null;
            if (this.state.preferredLocationFromDealer) {
                foundPreferred = preferredSpecialists.find(specialist =>
                    String(specialist.Installer_id) === String(this.state.preferredSpecialistFromDealer) &&
                    String(specialist.location_id || specialist.Location_id) === String(this.state.preferredLocationFromDealer)
                );
            }
            if (!foundPreferred) {
                foundPreferred = preferredSpecialists.find(specialist =>
                    String(specialist.Installer_id) === String(this.state.preferredSpecialistFromDealer)
                );
            }
            if (foundPreferred) {
                this.selectSpecialist(foundPreferred);
                return;
            }
        }

        // Count total selectable options: individual preferred + "Any" (if visible)
        // Must mirror the render-side "Any" condition exactly. A marine
        // dealership must never reach assignedTo = "0": the server's match-all
        // path filters on distance, category and status only — it never reads
        // marine capability — so the request would be offered to every car-only
        // shop in range, each offer writing a row.
        const hasAnyOption = this.state.vendorVisibility === "all" && this.state.filteredSpecialists.length > 0 && !isMarineDealer(this.props.dealerId);
        const totalOptions = preferredSpecialists.length + (hasAnyOption ? 1 : 0);

        // If only one selectable option, auto-select it to speed up data entry
        if (totalOptions === 1) {
            if (preferredSpecialists.length === 1) {
                this.selectSpecialist(preferredSpecialists[0]);
            } else if (hasAnyOption) {
                // "Any" is the only option — select it
                const anyLabel = `Any (${this.state.filteredSpecialists.length})`;
                this.setState({ selectedSpecialist: anyLabel, name: 'Any' });
                this.props.handleSpecialist("0", this.state.selectedVendorCategory);
            }
        }
    };

    // Handle vendor category selection
    handleVendorCategoryChange = async (event) => {
        const { value } = event.target;
        
        // Find the category configuration to get vendor_visibility_mode
        const categoryConfig = this.state.vendorCategories.find(cat => cat.category_name === value);
        const visibility = categoryConfig ? categoryConfig.vendor_visibility_mode : "all";
        
        this.setState({ 
            selectedVendorCategory: value,
            vendorVisibility: visibility,
            selectedSpecialist: "Specialist",
            name: "Select the specialist"
        });

        // Fetch specialists for the selected category
        await this.fetchSpecialistsByCategory(value);

        // Notify parent component about category change
        if (this.props.handleSpecialistCategory) {
            this.props.handleSpecialistCategory(value, visibility);
        }
    };

    // Component lifecycle - fetch categories when component mounts
    componentDidMount() {
        // On resume, remember the draft's specialist so the category/specialist
        // cascade preselects it rather than overriding it with the preferred vendor.
        const s = this.props.selectedSpecialist;
        if (s !== undefined && s !== null && String(s) !== "" && String(s) !== "Specialist") {
            this._pendingResumeSpecialistId = String(s);
        }
        if (this.props.dealerId) {
            this.fetchVendorCategories();
        } else {
            console.warn("SpecialistForm: dealerId prop is required");
        }
    }

    // Handle specialist selection
    handleSpecialistChange = (event) => {
        const { value } = event.target;
        //console.log ("Selected Specialist:", value);
        //console.log("Available Specialists:", this.state.filteredSpecialists);

        let returnValue = "0";
        let cleanedValue = value;

        // Check if the selection is "Any" with count, and clean it
        if (value.startsWith("Any (") && value.endsWith(")")) {
            cleanedValue = "Any";
            this.setState({ name: cleanedValue, selectedSpecialist: value });
            this.props.handleSpecialist(returnValue, this.state.selectedVendorCategory);
            return;
        }

        // Handle "Specialist" placeholder selection
        if (value === "Specialist") {
            this.setState({ name: "Select the specialist", selectedSpecialist: value });
            this.props.handleSpecialist(returnValue, this.state.selectedVendorCategory);
            return;
        }

        // Look up the specialist object from the display map
        const specialist = this.specialistDisplayMap[value];

        if (specialist) {
            // Extract the actual name without distance/rating info
            const actualName = specialist.Installer_Name || specialist.installer_name;
            returnValue = specialist.Installer_id || specialist.installer_id;

            this.setState({
                name: actualName,
                selectedSpecialist: value
            });

            // Notify parent component — pass location_id if present (multi-location discovery)
            this.props.handleSpecialist(returnValue, this.state.selectedVendorCategory, specialist.location_id || specialist.Location_id || null);
        } else {
            // Fallback: try to find by exact name match (for backwards compatibility)
            const installer = this.state.filteredSpecialists.find(special => special.Installer_Name === value);
            if (installer) {
                returnValue = installer.Installer_id;
            }

            this.setState({ name: value, selectedSpecialist: value });
            this.props.handleSpecialist(returnValue, this.state.selectedVendorCategory);
        }
    };

    // Helper method to build specialist display list and map
    buildSpecialistDisplayList = (specialists) => {
        // Sort by distance (closest first)
        const sortedSpecialists = specialists
            .filter(specialist => Number(specialist.dealer_preferred) === 1)
            .sort((a, b) => {
                const distA = a.Distance !== null && a.Distance !== undefined ? a.Distance : 999;
                const distB = b.Distance !== null && b.Distance !== undefined ? b.Distance : 999;
                return distA - distB;
            });

        // Check if user is a dealer
        const isDealer = this.props.userType === 'dealer';

        // Create display map for reverse lookup and format display strings
        const displayMap = {};
        const displayList = sortedSpecialists.map(specialist => {
            const baseName = specialist.Installer_Name || specialist.installer_name;
            const locationLabel = specialist.location_name || specialist.installer_address_city;
            const parts = [locationLabel ? `${baseName} - ${locationLabel}` : baseName];

            // Add distance in miles - for dealer users
            if (isDealer && specialist.Distance !== null && specialist.Distance !== undefined) {
                parts.push(`${Number(specialist.Distance).toFixed(1)} mi`);
            }

            // Add Google rating and review count - for dealer users
            if (isDealer) {
                if (specialist.google_rating && specialist.google_review_count) {
                    parts.push(`⭐ ${Number(specialist.google_rating).toFixed(1)} (${specialist.google_review_count})`);
                } else if (specialist.google_rating) {
                    parts.push(`⭐ ${Number(specialist.google_rating).toFixed(1)}`);
                }
            }

            const displayName = parts.join(' • ');
            displayMap[displayName] = specialist; // Store mapping
            return displayName;
        });

        return { displayList, displayMap };
    };

    render() {
        if (this.props.currentStep !== 1) {
            return null;
        }

        // Filtered HERE as well as at fetch time. The fetch happens once, on
        // mount, but a dealer-group user can switch the active dealership
        // afterwards — leaving car-only locations selectable for a marine
        // dealership, which is the dead end this filter exists to prevent.
        // Colocated with the "Any" suppression below so the two cannot disagree.
        const visibleSpecialists = filterSpecialistsForDealer(
          this.state.filteredSpecialists,
          isMarineDealer(this.props.dealerId)
        );

        // Build specialist options from filtered list
        let specialistList;

        if (this.state.loadingSpecialists) {
            specialistList = ["Loading..."];
            this.specialistDisplayMap = {}; // Clear the map when loading
        } else {
            // Build the display list and map
            const result = this.buildSpecialistDisplayList(visibleSpecialists);
            specialistList = result.displayList;
            this.specialistDisplayMap = result.displayMap; // Store in instance variable
        }

        // Add "Any" option with count of ALL specialists only if vendor_visibility_mode allows it and not loading
        // No "Any" for a marine dealership. /matchAll filters on distance, installer
        // category and status only — it joins installer_location but never reads
        // marine_enabled — so a boat with assignedTo "0" is offered to every car-only
        // shop in range, each offer writing a request_offered row. Forcing a concrete
        // marine-capable LOCATION closes that from the client.
        if (!this.state.loadingSpecialists && this.state.vendorVisibility === "all" && visibleSpecialists.length > 0 && !isMarineDealer(this.props.dealerId)) {
            const anyOptionWithCount = `Any (${visibleSpecialists.length})`;
            specialistList.unshift(anyOptionWithCount);
        }
        
        if (!this.state.loadingSpecialists) {
            specialistList.unshift("Specialist");
        }

        // Build category options
        const categoryOptions = this.state.loadingCategories 
            ? ["Loading..."]
            : this.state.vendorCategories.map(cat => cat.category_name);

        // Dynamic customer message based on current state
        let customerMessage;
        if (this.state.loadingSpecialists || this.state.loadingCategories) {
            customerMessage = "Loading specialists...";
        } else if (visibleSpecialists.length === 0) {
            customerMessage = "No specialists available";
        } else if (this.state.vendorVisibility === "preferred") {
            customerMessage = "Select your preferred specialist";
        } else if (this.state.vendorVisibility === "all") {
            customerMessage = "Select your preferred specialist or ANY";
        } else {
            // Default fallback - before any category is selected or processed
            customerMessage = "Select your specialist";
        }
        
        return (
            <React.Fragment>
                <form className="form-container p-5">
                    <div className="row justify-content-md-center">
                        <h5 className="text-center">{customerMessage}</h5>
                    </div>
                    
                    {/* Vendor Category Dropdown - Only show if more than 1 category */}
                    {!this.state.loadingCategories && this.state.vendorCategories.length > 1 && (
                        <div className="form-row justify-content-md-center">
                            <div className="form-group col-md-3 text-center">
                                <Select 
                                    name="vendorCategory"
                                    label="Vendor Category"
                                    options={categoryOptions}
                                    value={this.state.selectedVendorCategory}
                                    onChange={this.handleVendorCategoryChange}
                                />
                            </div>
                        </div>
                    )}

                    {/* Specialist Dropdown - REMOVED LABEL */}
                    <div className="form-row justify-content-md-center">
                        <div className="form-group col-md-3 text-center">
                            <Select 
                                name="specialist"
                                options={specialistList}
                                value={this.state.selectedSpecialist}
                                onChange={this.handleSpecialistChange}
                                disabled={this.state.loadingSpecialists || this.state.loadingCategories}
                            />
                        </div>
                    </div>

                    {/* Loading indicator */}
                    {this.state.loadingSpecialists && (
                        <div className="row justify-content-md-center">
                            <div className="col-md-6 text-center">
                                <small className="text-muted">
                                    Loading specialists...
                                </small>
                            </div>
                        </div>
                    )}

                    {/* No specialists found message */}
                    {!this.state.loadingSpecialists && visibleSpecialists.length === 0 && (
                        <div className="row justify-content-md-center">
                            <div className="col-md-6 text-center">
                                <small className="text-warning">
                                    {this.state.categoriesUnavailable === 'none'
                                        ? "This dealership has no service categories set up, so no specialists can be offered. An administrator needs to configure its categories before work can be requested."
                                        : this.state.categoriesUnavailable === 'error'
                                        ? "Could not load this dealership's service categories, so no specialists can be offered. Reload and try again."
                                        : "No specialists found for the selected category."}
                                </small>
                            </div>
                        </div>
                    )}
                </form>

                {(this.state.selectedSpecialist !== "Specialist" && 
                  this.state.selectedSpecialist !== "Loading...") && (
                    <div>
                        <h5 className="text-center bold p-5">Specialist Selected</h5>
                        <div className="col-lg-12 bold text-center">
                            <p>{this.state.name}</p>
                            <p>{this.state.email}</p>
                        </div>
                    </div>
                )}
            </React.Fragment>
        );
    }
}